import { useMemo, useState } from 'react'

import FileDownloadIcon from '@mui/icons-material/FileDownload'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { useFormContext, useWatch } from 'react-hook-form'

import { ControlledNumericFormat, ControlledSelector } from 'components/common/controller-wrapper'
import {
  BID_SPREAD_ANALYSIS_CHART_HEIGHT,
  BID_SPREAD_ANALYSIS_JOB_VALUE_FILTERS,
  BID_SPREAD_ANALYSIS_VIEWS,
  resolveBidSpreadAnalysisPeriodSlug,
} from 'components/reports/Metrics/constants/BidSpreadAnalysis'
import { useGetBidSpreadAnalysisExportRowsLazyQuery } from 'generated/graphql'

import { BidSpreadAnalysisHistogram } from '../components/types'
import { BidSpreadAnalysisChart } from './BidSpreadAnalysisChart'
import { BidSpreadAnalysisFilterValues } from './BidSpreadAnalysisFilterCriteria'
import { useBidSpreadAnalysisQueryInput } from './useBidSpreadAnalysisQueryInput'

interface Props {
  histogram?: BidSpreadAnalysisHistogram | null
  loading?: boolean
}

const csvValue = (value: string | number) => {
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export const BidSpreadAnalysisCard = ({ histogram, loading }: Props) => {
  const { control, setValue } = useFormContext<BidSpreadAnalysisFilterValues>()
  const view = useWatch({ control, name: 'view' })
  const jobValueFilter = useWatch({ control, name: 'jobValueFilter' })
  const isPercentage = view === 'percentage'
  const isCustomJobValue = jobValueFilter === 'custom'
  const [isExporting, setIsExporting] = useState(false)

  const { start, end, period, input } = useBidSpreadAnalysisQueryInput()
  const [fetchExportRows] = useGetBidSpreadAnalysisExportRowsLazyQuery()

  const buckets = useMemo(
    () => (isPercentage ? histogram?.percentageBuckets : histogram?.absoluteBuckets) ?? [],
    [histogram, isPercentage],
  )

  const summary = isPercentage ? histogram?.percentageSummary : histogram?.absoluteSummary

  const handleViewChange = (_: unknown, newView: BidSpreadAnalysisFilterValues['view']) => {
    if (newView !== null) {
      setValue('view', newView)
    }
  }

  const formatSpread = (value: number | undefined) =>
    value == null ? '-' : isPercentage ? `${value.toLocaleString()}%` : `£${value.toLocaleString()}`

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      const { data } = await fetchExportRows({ variables: { input } })
      const rows = data?.bidSpreadAnalysisExportRows ?? []

      const header = [
        'Job ID',
        'Highest bid',
        'Lowest bid',
        'Accepted price',
        'Absolute spread',
        'Percentage spread',
        'Vehicle type',
        'Service code',
        'Job value',
      ].join(',')

      const lines = rows.map((row) =>
        [
          row.bookingId,
          row.highestBidPrice,
          row.lowestBidPrice,
          row.acceptedBidPrice ?? '',
          row.absoluteSpread,
          row.percentageSpread ?? '',
          csvValue(row.vehicleType),
          csvValue(row.serviceCode),
          row.jobValue,
        ].join(','),
      )

      const csv = [header, ...lines].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const timestamp = format(new Date(), 'yyyyMMdd')
      const periodSlug = resolveBidSpreadAnalysisPeriodSlug(period, start, end)
      const link = document.createElement('a')
      link.href = url
      link.download = `bid_spread_${timestamp}_${periodSlug}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        mb={2}>
        <Box>
          <Typography variant='subtitle1' fontWeight={600}>
            Bid spread per job
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Distribution of jobs by the spread between their highest and lowest bid.
          </Typography>
        </Box>

        <Stack direction='row' spacing={2} alignItems='center'>
          <Stack alignItems='flex-end'>
            <Typography variant='caption' color='text.secondary'>
              Total jobs
            </Typography>
            {loading ? (
              <Skeleton width={60} height={32} />
            ) : (
              <Typography variant='h6' fontWeight={700}>
                {(histogram?.totalJobs ?? 0).toLocaleString()}
              </Typography>
            )}
          </Stack>

          <ToggleButtonGroup
            value={view}
            onChange={handleViewChange}
            color='primary'
            size='small'
            exclusive>
            {BID_SPREAD_ANALYSIS_VIEWS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <LoadingButton
            variant='outlined'
            size='small'
            startIcon={<FileDownloadIcon />}
            loading={isExporting}
            disabled={loading || (histogram?.totalJobs ?? 0) === 0}
            onClick={handleExportCsv}>
            Export CSV
          </LoadingButton>
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={BID_SPREAD_ANALYSIS_CHART_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <BidSpreadAnalysisChart
          buckets={buckets}
          xAxisLabel={isPercentage ? 'Bid spread (% of job value)' : 'Bid spread (£)'}
        />
      )}

      <Stack direction='row' spacing={2} useFlexGap flexWrap='wrap' sx={{ mt: 3 }}>
        {[
          { label: 'Average spread', value: summary?.averageSpread },
          { label: 'Median spread', value: summary?.medianSpread },
          { label: 'Min spread', value: summary?.minSpread },
          { label: 'Max spread', value: summary?.maxSpread },
        ].map((stat) => (
          <Paper
            key={stat.label}
            variant='outlined'
            sx={{
              flex: '1 1 150px',
              p: 2,
              borderRadius: 2,
              textAlign: 'center',
              bgcolor: 'action.hover',
              borderColor: 'divider',
            }}>
            <Typography variant='caption' color='text.secondary'>
              {stat.label}
            </Typography>
            <Typography variant='h5' fontWeight={700} color='primary.main'>
              {formatSpread(stat.value)}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        sx={{ mt: 2 }}>
        <ControlledSelector
          control={control}
          name='jobValueFilter'
          label='Job value'
          options={BID_SPREAD_ANALYSIS_JOB_VALUE_FILTERS}
          size='small'
          fullWidth={false}
          sx={{ minWidth: 180 }}
        />

        {isCustomJobValue && (
          <>
            <ControlledNumericFormat
              control={control}
              name='customMinJobValue'
              label='Min £'
              size='small'
              fullWidth={false}
              sx={{ width: 140 }}
            />
            <ControlledNumericFormat
              control={control}
              name='customMaxJobValue'
              label='Max £'
              size='small'
              fullWidth={false}
              sx={{ width: 140 }}
            />
          </>
        )}
      </Stack>

      <Stack
        direction='row'
        spacing={1}
        alignItems='flex-start'
        sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Box sx={{ color: 'text.secondary', display: 'flex', pt: 0.25 }}>
          <InfoOutlinedIcon fontSize='small' />
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary' display='block'>
            Bid spread is calculated as the highest bid minus the lowest bid received for each job.
            Toggle between absolute (£) and relative (%) spread to account for job value
            differences. Absolute spread shows the real £ variance, while percentage spread shows
            variance relative to job value, making it comparable across small and large jobs. Only
            jobs with an allocated bid are included in the percentage view.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
